import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import type { User } from '../../../types';
import type { PartnerRequirementType, RequirementCompletionStatus } from '../types';

export type TeacherProximity = 'same_city' | 'same_state' | 'location_unknown' | 'other_state';

export interface PartnerGeography {
  cities: string[];
  states: string[];
}

export interface PartnerTeacherCandidate {
  id: string;
  displayName: string;
  email: string | null;
  headshotUrl: string | null;
  state: string;
  city: string;
  neighborhood: string;
  subjects: string[];
  backgroundCheckCleared: boolean;
  overtureBgCheckFile: unknown;
  mandatoryReportingCertificationFile: unknown;
  ogTrainingComplete: boolean;
}

const STATE_PATTERN = /(?:,\s*|\b)([A-Z]{2})(?:\s+\d{5}(?:-\d{4})?)?\s*$/i;

function clean(value: unknown): string {
  return String(value ?? '').trim();
}

function normalizeState(value: unknown): string {
  const state = clean(value).toUpperCase();
  return /^[A-Z]{2}$/.test(state) ? state : '';
}

function stateFromText(value: unknown): string {
  return normalizeState(clean(value).match(STATE_PATTERN)?.[1]);
}

function cityFromAddress(value: unknown): string {
  const parts = clean(value).split(',').map(part => part.trim()).filter(Boolean);
  return parts.length >= 3 ? parts[parts.length - 2] : '';
}

function normalizeCity(value: unknown): string {
  return clean(value).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

export function derivePartnerGeography(metro: unknown, addresses: unknown[]): PartnerGeography {
  const cities = new Set<string>();
  const states = new Set<string>();
  const metroCity = normalizeCity(clean(metro).split(',')[0]);
  const metroState = stateFromText(metro);
  if (metroCity) cities.add(metroCity);
  if (metroState) states.add(metroState);
  for (const address of addresses) {
    const city = normalizeCity(cityFromAddress(address));
    const state = stateFromText(address);
    if (city) cities.add(city);
    if (state) states.add(state);
  }
  return { cities: [...cities], states: [...states] };
}

export function teacherProximity(
  teacher: PartnerTeacherCandidate,
  geography: PartnerGeography,
): TeacherProximity {
  const city = normalizeCity(teacher.city || teacher.neighborhood.split(',')[0]);
  const state = normalizeState(teacher.state) || stateFromText(teacher.neighborhood);
  if (city && geography.cities.includes(city)) return 'same_city';
  if (state && geography.states.includes(state)) return 'same_state';
  if (!state) return 'location_unknown';
  return 'other_state';
}

export function globalEvidenceForRequirement(
  teacher: PartnerTeacherCandidate,
  type: PartnerRequirementType,
): boolean {
  if (type === 'basic_background_check') {
    return teacher.backgroundCheckCleared || Boolean(teacher.overtureBgCheckFile);
  }
  if (type === 'mandated_reporter') return Boolean(teacher.mandatoryReportingCertificationFile);
  return false;
}

export function isActiveTeacherStatus(status: unknown): boolean {
  return !clean(status) || status === 'active' || status === 'sub';
}

export function partnerRequirementDisplayState(
  teacher: PartnerTeacherCandidate,
  type: PartnerRequirementType,
  status?: RequirementCompletionStatus,
): 'Verified' | 'In progress' | 'Evidence on file' | 'Needs action' {
  if (status === 'verified' || status === 'not_required') return 'Verified';
  if (status === 'in_progress' || status === 'submitted') return 'In progress';
  if (globalEvidenceForRequirement(teacher, type)) return 'Evidence on file';
  return 'Needs action';
}

export function filterAndRankTeachers(
  teachers: PartnerTeacherCandidate[],
  geography: PartnerGeography,
  search: string,
  scope: 'nearby' | 'all',
): PartnerTeacherCandidate[] {
  const needle = normalizeCity(search);
  const proximityOrder: Record<TeacherProximity, number> = {
    same_city: 0,
    same_state: 1,
    location_unknown: 2,
    other_state: 3,
  };
  return teachers
    .filter(teacher => !needle || normalizeCity(teacher.displayName).includes(needle))
    .filter(teacher => needle || scope === 'all' || teacherProximity(teacher, geography) !== 'other_state')
    .sort((left, right) => {
      const proximityDifference = proximityOrder[teacherProximity(left, geography)]
        - proximityOrder[teacherProximity(right, geography)];
      return proximityDifference || left.displayName.localeCompare(right.displayName);
    });
}

export async function listActivePartnerTeacherCandidates(): Promise<PartnerTeacherCandidate[]> {
  const [usersSnapshot, complianceSnapshot] = await Promise.all([
    getDocs(query(collection(db, 'users'), where('role', '==', 'teacher'))),
    getDocs(collection(db, 'teachers')),
  ]);
  const complianceById = new Map(complianceSnapshot.docs.map(snapshot => [snapshot.id, snapshot.data()]));
  return usersSnapshot.docs
    .map(snapshot => {
      const user = { id: snapshot.id, ...snapshot.data() } as User;
      const compliance = complianceById.get(snapshot.id) || {};
      const merged = { ...user, ...compliance } as User;
      return {
        id: snapshot.id,
        displayName: clean(merged.displayName) || clean(merged.email) || 'Unnamed teacher',
        email: merged.email || null,
        headshotUrl: merged.headshotUrl || null,
        state: normalizeState(merged.state || merged.shippingAddress?.state)
          || stateFromText(merged.neighborhood || merged.address),
        city: clean(merged.shippingAddress?.city),
        neighborhood: clean(merged.neighborhood),
        subjects: Array.isArray(merged.subjects) ? merged.subjects.map(clean).filter(Boolean) : [],
        backgroundCheckCleared: merged.backgroundCheckCleared === true,
        overtureBgCheckFile: merged.overtureBgCheckFile,
        mandatoryReportingCertificationFile: merged.mandatoryReportingCertificationFile,
        ogTrainingComplete: merged.ogTrainingComplete === true,
        staffStatus: merged.staffStatus || 'active',
      };
    })
    .filter(teacher => isActiveTeacherStatus(teacher.staffStatus))
    .map(({ staffStatus: _staffStatus, ...teacher }) => teacher)
    .sort((left, right) => left.displayName.localeCompare(right.displayName));
}
